import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useAuthStore } from '../stores/auth-store';

export function LoginPage() {
    const navigate = useNavigate();
    const { user, signInWithGoogle, error, loading } = useAuthStore();
    const [acceptedLegal, setAcceptedLegal] = useState(false);

    useEffect(() => {
        if (user) {
            navigate('/');
        }
    }, [user, navigate]);

    return (
        <div className="min-h-screen w-full flex items-center justify-center relative overflow-hidden bg-[#1e1e2e]">
            {/* Dynamic Background Effects */}
            <div className="absolute top-[-50%] left-[-50%] w-[200%] h-[200%] bg-[radial-gradient(circle_at_center,_#89b4fa10_0%,_transparent_25%)] animate-pulse" />
            <div className="absolute top-0 left-0 w-full h-full bg-[linear-gradient(to_bottom_right,_#1e1e2e_0%,_#11111b_100%)] opacity-90" />

            {/* Decorative Orbs */}
            <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#89b4fa] rounded-full mix-blend-screen filter blur-[128px] opacity-20 animate-blob" />
            <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-[#cba6f7] rounded-full mix-blend-screen filter blur-[128px] opacity-20 animate-blob" style={{ animationDelay: '2s' }} />

            {/* Main Card */}
            <div className="relative z-10 w-full max-w-md p-8 mx-4">
                <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-2xl shadow-2xl p-8 flex flex-col items-center text-center">

                    {/* Logo/Icon */}
                    <div className="w-16 h-16 bg-white rounded-xl flex items-center justify-center mb-6 shadow-lg transform rotate-3 hover:rotate-6 transition-transform duration-300 overflow-hidden">
                        <img src="/logo.webp" alt="UI Grace" className="w-12 h-12 object-contain" />
                    </div>

                    <h1 className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-white to-gray-400 mb-2">
                        UI Grace
                    </h1>
                    <p className="text-[#a6adc8] mb-8">
                        Sign in to start crafting beautiful interfaces designed by AI.
                    </p>

                    {/* Error Message */}
                    {error && (
                        <div className="w-full bg-red-500/10 border border-red-500/20 rounded-lg p-3 mb-6 text-red-200 text-sm">
                            {error}
                        </div>
                    )}

                    {/* Sign In Button */}
                    <button
                        onClick={() => signInWithGoogle(acceptedLegal)}
                        disabled={loading || !acceptedLegal}
                        className="group relative w-full flex items-center justify-center gap-3 px-6 py-3.5 bg-white text-[#1e1e2e] rounded-xl font-medium transition-all duration-200 hover:bg-[#cdd6f4] hover:shadow-lg hover:shadow-blue-500/20 disabled:opacity-70 disabled:cursor-not-allowed"
                    >
                        {loading ? (
                            <Loader2 className="w-5 h-5 animate-spin" />
                        ) : (
                            <img
                                src="https://www.google.com/favicon.ico"
                                alt="Google"
                                className="w-5 h-5 filter grayscale group-hover:grayscale-0 transition-all"
                            />
                        )}
                        <span>{loading ? 'Connecting...' : 'Continue with Google'}</span>
                    </button>

                    <label className="mt-5 w-full flex items-start gap-2 text-left cursor-pointer">
                        <input
                            type="checkbox"
                            checked={acceptedLegal}
                            onChange={(e) => setAcceptedLegal(e.target.checked)}
                            className="mt-0.5 h-4 w-4 rounded border-white/20 bg-white/5 text-[#89b4fa] focus:ring-[#89b4fa]"
                        />
                        <span className="text-xs text-[#cdd6f4] leading-relaxed">
                            I confirm that I have read and agree to the{' '}
                            <a href="/terms" className="text-[#89b4fa] hover:underline">
                                Terms of Service
                            </a>{' '}
                            and{' '}
                            <a href="/privacy" className="text-[#89b4fa] hover:underline">
                                Privacy Policy
                            </a>
                            .
                        </span>
                    </label>

                    <div className="mt-4 w-full rounded-lg border border-white/10 bg-white/5 p-3 text-left">
                        <p className="text-[11px] leading-relaxed text-[#bac2de]">
                            API keys provided for AI generation are transmitted over HTTPS, stored in encrypted form on
                            the server, and used only for authorized generation requests on your behalf. Keys are not
                            stored in browser local storage and can be revoked or replaced by you at any time.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}
